import './pop-page.scss';

import React, {
  ComponentProps,
  CSSProperties,
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import cls from 'classnames';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { AppState } from 'core/redux/reducers';
import addCss from 'dom-helpers/css';
import qsa from 'dom-helpers/querySelectorAll';

interface IProps extends ComponentProps<'div'> {
  children: React.ReactNode;
  scrollableContent?: boolean;
  hide?: boolean;
  force?: boolean;
  isModal?: boolean;
  onHide?: () => void;
  autoDisableScroll?: boolean;

  header?: React.ReactNode;
  footer?: React.ReactNode;

  disable?: boolean;

  whiteBackdrop?: boolean;
  backdrop?: boolean;
  // className?: string
  // id?: string
}

const PopUpPage = forwardRef(function (
  {
    children,
    scrollableContent,
    hide,
    force,
    isModal,
    onHide,
    autoDisableScroll,
    className,
    header,
    footer,
    disable,
    style,
    whiteBackdrop = false,
    backdrop = true,
    ...htmlAttr
  }: IProps,
  fRef: ForwardedRef<HTMLDivElement>,
) {
  const deviceType = useSelector((state: AppState) => state.deviceType);

  const [rootElement, setRootElement] = useState<HTMLElement[]>([]);
  const [hideStyle, setHideStyle] = useState<CSSProperties>({
    display: hide ? 'none' : 'block',
    ...style,
  });
  const [isScrollActive, setIsScrollActive] = useState(true);

  useEffect(() => {
    setHideStyle({
      ...style,
      display: hide ? 'none' : 'block',
    });
  }, [hide, style]);

  const toggleDisableScroll = useCallback(
    (shouldDisabled: boolean) => {
      if (!rootElement) {
        return;
      }

      // when shouldDisabled is true and currently scroll state is not active skip
      if (shouldDisabled && !isScrollActive) {
        return;
      }

      // when shouldDisabled is false and currently scroll state is active skip
      if (!shouldDisabled && isScrollActive) {
        return;
      }

      rootElement.forEach(el => {
        addCss(el, {
          overflow: shouldDisabled ? 'hidden' : '',
        });
      });

      // Note: we can add new props called rootElements and
      //  then apply overflow hidden css to disabled scroll
      // newElPropsHere.forEach(el => {
      //   addCss(el, {
      //     overflow: shouldDisabled ? 'hidden' : '',
      //   })
      // })

      setIsScrollActive(!shouldDisabled);
    },
    [isScrollActive, rootElement],
  );

  const onClickClose = () => {
    toggleDisableScroll(false);
    if (onHide) {
      onHide();
    }
  };

  useEffect(() => {
    if (force) {
      window.scroll({ top: 0 });
    }
  }, [force]);

  useEffect(() => {
    if (rootElement.length === 0) {
      setRootElement(qsa(document, 'html'));
    }

    if (!autoDisableScroll) {
      return;
    }
    toggleDisableScroll(!hide && deviceType === 'mobile');

    return () => {
      toggleDisableScroll(false);
    };
  }, [
    autoDisableScroll,
    deviceType,
    hide,
    rootElement.length,
    toggleDisableScroll,
  ]);

  return (
    <div
      {...htmlAttr}
      ref={fRef}
      style={hideStyle}
      className={cls(
        'pop-page',
        disable && 'pop-page--disabled',
        force && 'pop-page--force',
        force && 'pop-page--always',
        className,
        isModal && 'pop-page--modal modal',
      )}
    >
      {isModal ? (
        <div className="modal-background" onClick={onClickClose} />
      ) : (
        <div
          className={cls(
            'pop-page__backdrop',
            whiteBackdrop && 'pop-page__backdrop--white',
            !backdrop && 'hide',
          )}
        />
      )}

      {header}
      <div className="pop-page-outer">
        {scrollableContent ? (
          <div
            className={classNames(
              'pop-page-content',
              isModal && 'notification',
              backdrop && 'pop-page-content--has-backdrop',
            )}
          >
            {isModal ? (
              <button
                className="delete"
                aria-label="close"
                onClick={onClickClose}
              />
            ) : null}
            {children}
          </div>
        ) : (
          children
        )}
      </div>
      {footer}
    </div>
  );
});

PopUpPage.defaultProps = {
  scrollableContent: true,
  className: '',
  force: false,
  isModal: false,
  autoDisableScroll: false,
  header: null,
  footer: null,
};

export default PopUpPage;
